import jsPDF from "jspdf";
//import autoTable from "jspdf-autotable";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { viewEmployeeMonthlySalary, viewEmployeeSalary } from "../../http";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Payslip = () => {
  const { user } = useSelector((state) => state.authSlice);
  const [salaryData, setSalaryData] = useState(null);
  const [grossSalaryData, setGrossSalaryData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Custom Input for DatePicker to look like a professional dropdown
  const CustomDateInput = ({ value, onClick }) => (
    <div
      className="d-flex align-items-center justify-content-between bg-white border rounded px-3 py-2 shadow-sm"
      onClick={onClick}
      style={{ cursor: "pointer", minWidth: "220px", transition: "all 0.2s" }}
      onMouseOver={(e) => (e.currentTarget.style.borderColor = "#007bff")}
      onMouseOut={(e) => (e.currentTarget.style.borderColor = "#dee2e6")}
    >
      <div className="d-flex align-items-center">
        <i className="bi bi-calendar3 text-primary me-2"></i>
        <div>
          <small className="text-muted d-block" style={{ fontSize: "0.7rem", lineHeight: "1" }}>Payroll Period</small>
          <span className="fw-bold text-dark">{value}</span>
        </div>
      </div>
      <i className="bi bi-chevron-down text-muted small ms-3"></i>
    </div>
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch both monthly salary and gross salary data
        const [monthlyRes, grossRes] = await Promise.all([
          viewEmployeeMonthlySalary({
            id: user.id,
            year: selectedYear,
            month: selectedMonth,
          }),
          viewEmployeeSalary({ employeeID: user.id }),
        ]);

        console.log("Monthly Salary Response:", monthlyRes);
        console.log("Gross Salary Response:", grossRes);

        const { data: monthlyData } = monthlyRes;
        const { data: grossData } = grossRes;

        // Handle monthly salary data
        const salaryInfo = Array.isArray(monthlyData)
          ? monthlyData[0]
          : monthlyData;
        const grossInfo = Array.isArray(grossData) ? grossData[0] : grossData;

        if (salaryInfo && grossInfo) {
          // Store gross salary components
          setGrossSalaryData(grossInfo);

          // Fixed Earnings using monthly salary data
          const fixedEarnings = {
            Basic: salaryInfo.basic || 0,
            HRA: salaryInfo.hra || 0,
            MedicalAllowance: salaryInfo.medicalAllowance || 0,
            ConveyanceAllowance: salaryInfo.conveyanceAllowance || 0,
            BookAllowance: salaryInfo.bookAllowance || 0,
            SpecialPeriodicAllowance: salaryInfo.specialPeriodicAllowance || 0,
            PLI: salaryInfo.pli || 0,
            Others: salaryInfo.others || 0,
            employeePf: salaryInfo.employeePf || 0,
          };

          // Total Fixed Earnings
          const totalFixedEarnings = Object.values(fixedEarnings).reduce(
            (a, b) => a + b,
            0
          );

          // Deductions from monthly salary data
          const deductions = {
            PF: salaryInfo.employeePf || 0,
            PF2: salaryInfo.employerPf || 0,
            TDS: salaryInfo.tds || 0,
            OtherDeduction: salaryInfo.otherDeduction || 0,
            LoanAdvance: salaryInfo.loanAdvance || 0,
            pt: salaryInfo.pt || 0,
          };

          const totalDeductions = Object.values(deductions).reduce(
            (a, b) => a + b,
            0
          );

          // Earned Salary = Sum of monthly salary components (no pro-rata logic)
          const earnedSalary = totalFixedEarnings;

          // Final payable salary
          const payableSalary = earnedSalary - totalDeductions;

          setSalaryData({
            ...salaryInfo,
            fixedEarnings,
            deductions,
            totalFixedEarnings,
            earnedSalary,
            totalDeductions,
            payableSalary,
          });
        } else {
          console.error("Salary data structure:", { salaryInfo, grossInfo });
          setSalaryData(null);
          // Only show toast if it's not the initial fetch failing for a legitimate "no data" case
          if (monthlyData && monthlyData.length === 0) {
            // toast.info("No salary record found for this month."); // Keeping it quiet for better UX
          }
        }
      } catch (error) {
        console.error("Error fetching salary data:", error);
        toast.error(`Error fetching salary data: ${error.message}`);
      }
    };

    fetchData();
  }, [user.id, selectedMonth, selectedYear]);

  // Helper function to safely format numbers
  const formatAmount = (value) => {
    const number = parseFloat(value || 0);
    return number.toFixed(2);
  };

  const generatePDF = () => {
    if (!salaryData) return;

    const doc = new jsPDF();

    // Company Header in Blue
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 83, 156);
    doc.text(
      "NEOPHYTE AMBIENT INTELLIGENCE PRIVATE LIMITED",
      doc.internal.pageSize.width / 2,
      15,
      { align: "center" }
    );

    // Company Address
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
    doc.text(
      "Shop No.6, Vishwakarma Tower, Sector 21, Kharghar, Navi Mumbai, MH 410210 IN",
      doc.internal.pageSize.width / 2,
      22,
      { align: "center" }
    );

    doc.text(
      "GSTIN -27AANCA8361J2Z5  Contact - 7606890935",
      doc.internal.pageSize.width / 2,
      29,
      { align: "center" }
    );

    // Monthly Pay Slip Title in Blue
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 83, 156);
    doc.text("Monthly Pay Slip", doc.internal.pageSize.width / 2, 38, {
      align: "center",
    });

    // Month and Year
    doc.setTextColor(0, 0, 0);
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const monthYear = `${monthNames[selectedMonth - 1]} ${selectedYear}`;
    doc.setFontSize(11);
    doc.text(monthYear, doc.internal.pageSize.width / 2, 46, {
      align: "center",
    });

    // Employee Details Section - Left Column
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const leftColX = 20;
    let yPos = 60;

    doc.text(`Employee ID : ${user.username}`, leftColX, yPos);
    yPos += 8;
    doc.text(`Employee Name : ${user.name}`, leftColX, yPos);
    yPos += 8;
    doc.text(`Designation : ${salaryData.designation || "IT"}`, leftColX, yPos);
    yPos += 8;
    doc.text(`UAN : ${salaryData.uan || ""}`, leftColX, yPos);

    // Employee Details Section - Right Column
    const rightColX = 120;
    yPos = 60;

    doc.text(`Total Leave : ${salaryData.totalLeave || ""}`, rightColX, yPos);
    yPos += 8;
    doc.text(
      `Allowed Leave : ${grossSalaryData.allowedLeaves || ""}`,
      rightColX,
      yPos
    );
    yPos += 8;
    doc.text(`Present Day : ${salaryData.presentDay || ""}`, rightColX, yPos);
    yPos += 8;
    doc.text(`Holidays : ${salaryData.holidays || ""}`, rightColX, yPos);
    yPos += 8;
    doc.text(`Total Paid days: ${salaryData.paidDays || 30}`, rightColX, yPos);
    yPos += 8;
    // Total Paid Days
    // doc.text(
    //   `Total Paid Days: ${salaryData.paidDays || 30}`,
    //   leftColX,
    //   yPos + 8
    // );

    // Table Headers
    yPos = 100;
    doc.setFont("helvetica", "bold");
    doc.text("Earnings", leftColX, yPos);
    doc.text("Deductions", 120, yPos);
    yPos += 8;

    // Sub Headers
    doc.setFont("helvetica", "normal");
    doc.text("Salary Head", leftColX, yPos);
    doc.text("Gross", 70, yPos);
    doc.text("Earning", 95, yPos);
    doc.text("Salary Head", 120, yPos);
    doc.text("Amount (Rs.)", 175, yPos);
    yPos += 8;

    // Table Content
    doc.setFont("helvetica", "normal");
    const rowSpacing = 8;

    // Helper function for salary rows
    const addSalaryRow = (label1, gross, earning, label2, deduction) => {
      doc.text(label1, leftColX, yPos);
      doc.text(formatCurrency(gross), 70, yPos);
      doc.text(formatCurrency(earning), 95, yPos);
      doc.text(label2, 120, yPos);
      doc.text(deduction, 175, yPos);
      yPos += rowSpacing;
    };

    // Add salary rows
    addSalaryRow(
      "Basic",
      grossSalaryData?.basic || 0,
      salaryData.basic,
      "PF (12% of Basic)",
      formatCurrency(salaryData.employeePf)
    );

    addSalaryRow(
      "HRA",
      grossSalaryData?.hra || 0,
      salaryData.hra,
      "Insurance/ESI",
      "-"
    );

    addSalaryRow(
      "Medical Allowance",
      grossSalaryData?.medicalAllowance || 0,
      salaryData.medicalAllowance,
      "Professional Tax",
      "200.00"
    );

    addSalaryRow(
      "Conveyance Allowance",
      grossSalaryData?.conveyanceAllowance || 0,
      salaryData.conveyanceAllowance,
      "Advance Tax (TDS)",
      "-"
    );

    addSalaryRow(
      "Book & Periodical Allowance",
      grossSalaryData?.bookAllowance || 0,
      salaryData.bookAllowance,
      "Loan/Advance",
      "-"
    );

    addSalaryRow(
      "Special Personal Allowance",
      grossSalaryData?.specialPeriodicAllowance || 0,
      salaryData.specialPeriodicAllowance,
      "Other Deduction",
      "-"
    );

    addSalaryRow(
      "PLI (Incentive)",
      grossSalaryData?.pli || 0,
      salaryData.pli || 0,
      "",
      ""
    );
    addSalaryRow(
      "Others",
      grossSalaryData?.others || 0,
      salaryData.others || 0,
      "",
      ""
    );

    // Totals
    yPos += 4;
    doc.setFont("helvetica", "bold");
    doc.text("Total Earnings", leftColX, yPos);
    doc.text(formatCurrency(salaryData.earnedSalary), 95, yPos);
    doc.text("Total Deductions", 120, yPos);
    doc.text(formatCurrency(salaryData.totalDeduction), 175, yPos);
    yPos += 16;

    // Net Salary
    doc.text("Net Salary", leftColX, yPos);
    doc.text(formatCurrency(salaryData.payableSalary), 95, yPos);
    yPos += 16;

    // Net Salary in Words
    doc.setFont("helvetica", "normal");
    doc.text("Net Salary In Words :", leftColX, yPos);
    yPos += 8;

    // Get salary in words
    const salaryInWords = numberToWords(salaryData.payableSalary) + " Only.";
    doc.text(salaryInWords, leftColX, yPos);
    yPos += 16;

    // Payment Details
    doc.text(
      `Payment Date- ${new Date().toLocaleDateString("en-GB")}`,
      leftColX,
      yPos
    );
    doc.text("Payment Credit : Bank Account", leftColX, yPos + 8);

    // Signature Lines
    yPos += 30;
    doc.text("Signature of Employer", 40, yPos);
    doc.text("Signature of Employee", 150, yPos);

    doc.save("monthly-payslip.pdf");
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const pt = selectedMonth === 2 ? 300 : 200;

  // Helper function to convert number to words
  const numberToWords = (num) => {
    const units = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
    ];
    const teens = [
      "",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "Ten",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    // Format the number to have 2 decimal places
    const formattedNum = num.toFixed(2);
    const parts = formattedNum.toString().split(".");

    let result = "";

    // Convert the whole number part
    const wholePart = parseInt(parts[0]);
    if (wholePart === 0) {
      result = "Zero";
    } else {
      // Process thousands
      if (wholePart >= 1000) {
        const thousands = Math.floor(wholePart / 1000);
        result +=
          (thousands > 9 ? numberToWords(thousands) : units[thousands]) +
          " Thousand ";
        if (wholePart % 1000 !== 0) result += "";
      }

      // Process hundreds
      const remainder = wholePart % 1000;
      if (remainder >= 100) {
        const hundreds = Math.floor(remainder / 100);
        result += units[hundreds] + " Hundred ";
        if (remainder % 100 !== 0) result += "";
      }

      // Process tens and units
      const tensAndUnits = remainder % 100;
      if (tensAndUnits > 0) {
        if (tensAndUnits < 10) {
          result += units[tensAndUnits];
        } else if (tensAndUnits < 20) {
          result += teens[tensAndUnits - 10];
        } else {
          const tensDigit = Math.floor(tensAndUnits / 10);
          const unitsDigit = tensAndUnits % 10;
          result += tens[tensDigit];
          if (unitsDigit > 0) {
            result += "-" + units[unitsDigit];
          }
        }
      }
    }

    // Add the word Rupees
    result += " Rupees";

    // Convert the decimal part (paise)
    const decimalPart = parseInt(parts[1]);
    if (decimalPart > 0) {
      if (decimalPart < 10) {
        result += " and " + units[decimalPart] + "0 Paisa";
      } else if (decimalPart < 20) {
        result += " and " + teens[decimalPart - 10] + " Paisa";
      } else {
        const tensDigit = Math.floor(decimalPart / 10);
        const unitsDigit = decimalPart % 10;
        if (unitsDigit === 0) {
          result += " and " + tens[tensDigit] + " Paisa";
        } else {
          result +=
            " and " + tens[tensDigit] + "-" + units[unitsDigit] + " Paisa";
        }
      }
    }

    return result;
  };

  // Removed the early return to always show header and date picker

  return (
    <div
      className="container-fluid"
      style={{ marginLeft: "250px", width: "calc(100% - 250px)" }}>
      <div className="card">
        <div className="card-header bg-primary text-white text-center">
          <h2 className="h4 mb-0">Employee Payslip</h2>
        </div>
        <div className="card-body">
          <div className="d-flex justify-content-end mb-4">
            <DatePicker
              selected={new Date(selectedYear, selectedMonth - 1)}
              onChange={(date) => {
                setSelectedMonth(date.getMonth() + 1);
                setSelectedYear(date.getFullYear());
              }}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              customInput={<CustomDateInput />}
            />
          </div>
          {!salaryData ? (
            <div className="text-center py-5">
              <i className="bi bi-info-circle text-muted mb-3" style={{ fontSize: "3rem" }}></i>
              <h5 className="text-muted">No salary record found for {[
                "January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"
              ][selectedMonth - 1]} {selectedYear}</h5>
              <p className="text-secondary small">Please select a different month or contact your administrator.</p>
            </div>
          ) : (
            <>
              <div className="row mb-3">
                <div className="col-md-6">
                  <p>
                    <strong>Name:</strong> {user.name}
                  </p>
                  <p>
                    <strong>Employee ID:</strong> {user.username}
                  </p>
                  <p>
                    <strong>Department:</strong> IT
                  </p>
                  <p>
                    <strong>Net Salary:</strong> ₹
                    {formatAmount(salaryData.payableSalary)}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Pay Period:</strong> {[
                      "January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"
                    ][selectedMonth - 1]} {selectedYear}
                  </p>
                  <p>
                    <strong>Generated On:</strong> {new Date().toLocaleDateString()}
                  </p>
                </div>
              </div>

              <table
                className="table payslip-table"
                style={{ border: "2px solid black" }}>
                <thead className="table">
                  <tr>
                    <th colSpan="3">Earnings</th>
                    <th colSpan="2">Deductions</th>
                  </tr>
                  <tr>
                    <th>Salary Head</th>
                    <th>Gross</th>
                    <th>Earnings</th>
                    <th>Salary Head</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Basic</td>
                    <td>
                      ₹{formatAmount(grossSalaryData?.basic || salaryData.basic)}
                    </td>
                    <td>₹{formatAmount(salaryData.basic)}</td>
                    <td>PF (Employee)</td>
                    <td>₹{formatAmount(salaryData.employeePf)}</td>
                  </tr>
                  <tr>
                    <td>HRA</td>
                    <td>₹{formatAmount(grossSalaryData?.hra || salaryData.hra)}</td>
                    <td>₹{formatAmount(salaryData.hra)}</td>
                    <td>Professional Tax</td>
                    <td>₹{pt}</td>
                  </tr>
                  <tr>
                    <td>Medical Allowance</td>
                    <td>
                      ₹
                      {formatAmount(
                        grossSalaryData?.medicalAllowance ||
                        salaryData.medicalAllowance
                      )}
                    </td>
                    <td>₹{formatAmount(salaryData.medicalAllowance)}</td>
                    <td>TDS</td>
                    <td>₹{formatAmount(salaryData.tds)}</td>
                  </tr>
                  <tr>
                    <td>Conveyance Allowance</td>
                    <td>
                      ₹
                      {formatAmount(
                        grossSalaryData?.conveyanceAllowance ||
                        salaryData.conveyanceAllowance
                      )}
                    </td>
                    <td>₹{formatAmount(salaryData.conveyanceAllowance)}</td>
                    <td>Other Deductions</td>
                    <td>₹{formatAmount(salaryData.otherDeduction)}</td>
                  </tr>
                  <tr>
                    <td>Book Allowance</td>
                    <td>
                      ₹
                      {formatAmount(
                        grossSalaryData?.bookAllowance || salaryData.bookAllowance
                      )}
                    </td>
                    <td>₹{formatAmount(salaryData.bookAllowance)}</td>
                    <td>Loan/Advance</td>
                    <td> ₹{formatAmount(salaryData.loanAdvance)}</td>
                  </tr>
                  <tr>
                    <td>Special Periodic Allowance</td>
                    <td>
                      ₹
                      {formatAmount(
                        grossSalaryData?.specialPeriodicAllowance ||
                        salaryData.specialPeriodicAllowance
                      )}
                    </td>
                    <td>₹{formatAmount(salaryData.SpecialPeriodicAllowance)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>PLI</td>
                    <td>₹{formatAmount(grossSalaryData?.pli || salaryData.pli)}</td>
                    <td>₹{formatAmount(salaryData.pli)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>Others</td>
                    <td>
                      ₹{formatAmount(grossSalaryData?.others || salaryData.others)}
                    </td>
                    <td>₹{formatAmount(salaryData.others)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                  <tr>
                    <td>Total Earnings</td>
                    <td></td>
                    <td>₹{formatAmount(salaryData.earnedSalary)}</td>
                    <td>Total Deductions</td>
                    <td>₹{formatAmount(salaryData.totalDeductions)}</td>
                  </tr>
                </tbody>
              </table>

              <div className="row mt-4">
                <div className="col-md-6">
                  <p>
                    <strong>
                      Net Payable Salary: ₹{formatAmount(salaryData.payableSalary)}
                    </strong>
                  </p>
                  {salaryData.employerPf > 0 && (
                    <p>
                      <strong>
                        Employer PF Contribution: ₹
                        {formatAmount(salaryData.employerPf)}
                      </strong>
                    </p>
                  )}
                </div>
                <div className="col-md-6 text-end">
                  <button className="btn btn-primary" onClick={generatePDF}>
                    <i className="bi bi-download me-2"></i>
                    Download Payslip
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payslip;
